import { type ComponentType } from 'react'
import { type z } from 'zod'

import { type schemas } from '~interface/zod-schema'

import { type JsonSchemaProperty } from '../JsonSchemaBuilder'

export function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  )
}

export function getErrorMessage(error: unknown): string {
  if (isErrorWithMessage(error)) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// Zod-inferred types from auto-generated schema
export type AuthType = z.infer<typeof schemas.AuthTypeEnum>
export type HTTPMethod = z.infer<typeof schemas.HTTPMethodEnum>
export type DataFormatType = z.infer<typeof schemas.DataFormatEnum>
export type ParameterLocation = z.infer<typeof schemas.ParameterLocationEnum>
export type ParameterType = z.infer<typeof schemas.ParameterTypeEnum>
export type IntegrationType = z.infer<typeof schemas.Request311Enum>
export type IntegrationSurface = z.infer<typeof schemas.IntegrationSurfaceEnum>

export const ALL_SURFACES: IntegrationSurface[] = [
  'CHAT',
  'VOICE',
  'SEARCH',
  'ASSISTANT',
  'WORKFLOW',
]

export const DRAFT_SURFACES: IntegrationSurface[] = ['CHAT', 'ASSISTANT']

export const DRAFT_SURFACE_TOOLTIP =
  'Draft integrations can only be used in Chat and Assistant chats in test mode.'

export const SURFACE_LABELS: Record<IntegrationSurface, string> = {
  CHAT: 'Chat',
  VOICE: 'Voice',
  SEARCH: 'Search',
  ASSISTANT: 'Assistant',
  WORKFLOW: 'Workflows',
}

// API request/response types inferred from zod schema
export type TestCustomEndpointRequest = z.infer<typeof schemas.TestCustomEndpointViewRequestData>
export type TestCustomEndpointResponse = z.infer<typeof schemas.TestCustomEndpointViewResponseData>
export type GenerateCustomIntegrationRequest = z.infer<
  typeof schemas.GenerateCustomIntegrationViewRequestData
>
export type GenerateCustomIntegrationResponse = z.infer<
  typeof schemas.GenerateCustomIntegrationViewResponseData
>
export type TestHostedIntegrationCredentialsRequest = z.infer<
  typeof schemas.TestHostedIntegrationCredentialsViewRequestData
>
export type TestHostedIntegrationCredentialsResponse = z.infer<
  typeof schemas.TestHostedIntegrationCredentialsViewResponseData
>
export type TestIntegrationEndpointsRequest = z.infer<
  typeof schemas.TestIntegrationEndpointsViewRequestData
>
export type TestIntegrationEndpointsResponse = z.infer<
  typeof schemas.TestIntegrationEndpointsViewResponseData
>
export type EndpointSchemaType = z.infer<typeof schemas.EndpointSchema>
export type ParameterSchemaType = z.infer<typeof schemas.ParameterSchema>
export type UpdateCustomIntegrationRequest = z.infer<
  typeof schemas.UpdateCustomIntegrationViewRequestData
>
export type UpdateCustomIntegrationResponse = z.infer<
  typeof schemas.UpdateCustomIntegrationViewResponseData
>

// API Connection/Endpoint list types
export type ListAPIConnectionsRequest = z.infer<typeof schemas.ListAPIConnectionsViewRequestData>
export type ListAPIConnectionsResponse = z.infer<typeof schemas.ListAPIConnectionsViewResponseData>
export type DeleteAPIConnectionRequest = z.infer<typeof schemas.DeleteAPIConnectionViewRequestData>
export type DeleteAPIConnectionResponse = z.infer<
  typeof schemas.DeleteAPIConnectionViewResponseData
>
export type UpdateAPIEndpointsRequest = z.infer<typeof schemas.UpdateAPIEndpointsViewRequestData>
export type UpdateAPIEndpointsResponse = z.infer<typeof schemas.UpdateAPIEndpointsViewResponseData>
export type UpdateEndpointActiveRequest = z.infer<
  typeof schemas.UpdateEndpointActiveViewRequestData
>
export type UpdateEndpointActiveResponse = z.infer<
  typeof schemas.UpdateEndpointActiveViewResponseData
>
export type EndpointUpdate = z.infer<typeof schemas.EndpointUpdate>
export type EndpointConfigUpdate = z.infer<typeof schemas.EndpointConfigUpdate>
export type UpdateEndpointConfigsRequest = z.infer<
  typeof schemas.UpdateEndpointConfigsViewRequestData
>
export type UpdateEndpointConfigsResponse = z.infer<
  typeof schemas.UpdateEndpointConfigsViewResponseData
>

// API Connection/Endpoint types - now inferred from zod schema
export type APIEndpointParameter = z.infer<typeof schemas.APIEndpointParameter>
export type APIEndpointConfig = z.infer<typeof schemas.APIEndpointConfig>
export type APIEndpoint = z.infer<typeof schemas.APIEndpoint>
export type APIConnection = z.infer<typeof schemas.APIConnection>

// Endpoint test result from TestIntegrationEndpoints API
// Note: zod schema uses z.unknown() for results array, so we define the shape here
export interface HostedEndpointTestResult {
  tool_name: string
  name: string
  success: boolean | null
  status_code: number | null
  message: string
  error_details?: string | null
  testable: boolean
}

// UI-specific types (not in zod schema - frontend state management)
export type ConfigTab = 'params' | 'headers' | 'body' | 'settings'
export type BodyEditorMode = 'raw' | 'schema'
export type WizardStep = 'basic-info' | 'configure' | 'test' | 'agent-settings'

export interface XmlNamespacePreset {
  id: string
  label: string
  description: string
  rootTag: string
  namespaces: Record<string, string>
  template: string
}

export interface KeyValueRow {
  id: string
  enabled: boolean
  key: string
  value: string
  description: string
}

export interface ParameterRow {
  id: string
  enabled: boolean
  name: string
  location: ParameterLocation
  parameterType: ParameterType
  value: string
  description: string
  required: boolean
  jsonSchema?: JsonSchemaProperty
  requiredParams?: string[]
  validationRules?: Record<string, unknown>
  expanded?: boolean
}

export type VerificationMatchType = 'exact' | 'fuzzy'

/** A field the verification sub-agent checks the user against before data is returned. */
export interface VerificationFieldRow {
  id: string
  name: string
  responsePath: string
  matchType: VerificationMatchType
}

/** Shape persisted under `validation_rules.user_verification` on an endpoint. */
export interface UserVerificationConfig {
  enabled: boolean
  fields: {
    name: string
    response_path: string
    match_type: VerificationMatchType
  }[]
  /** Overrides the name of the generated identity parameter. Set outside this editor. */
  identity_parameter_name?: string
}

export interface ResponseMappingRow {
  id: string
  outputField: string
  sourcePath: string
  description: string
}

export interface EndpointConfig {
  id: string
  name: string
  description: string
  httpMethod: HTTPMethod
  endpointUrl: string
  parameters: ParameterRow[]
  headers: KeyValueRow[]
  body: string
  bodySchema: JsonSchemaProperty
  bodyRequiredPaths: string[]
  bodyEditorMode: BodyEditorMode
  requestFormat: DataFormatType
  responseFormat: DataFormatType
  xmlNamespacePreset: string
  timeout: number
  rateLimit: number | null
  validationRules: Record<string, unknown>
  verificationEnabled: boolean
  verificationFields: VerificationFieldRow[]
  verificationSettings: Record<string, unknown>
  responseMappings: ResponseMappingRow[]
  toolName: string
  toolDescription: string
  toolEnabled: boolean
  active: boolean
}

export interface AuthConfig {
  headerName?: string
  prefix?: string
  keyValue?: string
  tokenValue?: string
  username?: string
  password?: string
  clientId?: string
  clientSecret?: string
  tokenUrl?: string
  scopes?: string
  grantType?: 'client_credentials' | 'password'
  paramName?: string
  paramValue?: string
  // OAuth2 token injection customization
  tokenInjection?: {
    headerName?: string
    headerPrefix?: string
  }
  // Extra parameters for token request (e.g., agency_name, environment)
  extraTokenParams?: KeyValueRow[]
}

export interface ConnectionConfig {
  name: string
  description: string
  baseUrl: string
  authType: AuthType
  authConfig: AuthConfig
  defaultHeaders: KeyValueRow[]
  useIpProxy: boolean
  browserRegion?: string | null
}

// Use the zod-inferred TestResult type
export type TestResult = {
  status: number | null
  statusText: string
  body: string
  time: number
  error: string | null
}

export interface CustomConnectionModalProps {
  open: boolean
  onClose: () => void
  onExited?: () => void
  onSave: () => void
  editConnection?: APIConnection
}

export interface IntegrationFieldsProps {
  config: IntegrationConfig
  onChange: (config: IntegrationConfig) => void
}

export type IntegrationConfig = Record<string, unknown>

export interface IntegrationFormFieldOption {
  value: string
  label: string
  helperText?: string
}

export interface IntegrationFormField {
  name: string
  label: string
  placeholder: string
  helperText?: string
  required: boolean
  type: 'text' | 'password' | 'url' | 'select'
  options?: IntegrationFormFieldOption[]
  defaultValue?: string
}

export interface IntegrationAPIConfig {
  createEndpoint: string
  buildPayload: (
    formData: Record<string, string>,
    config: IntegrationConfig,
    organizationId: string,
    organizationIdentityId: string,
  ) => Record<string, unknown>
  authTokenName: string
  buildTestCredentials?: (formData: Record<string, string>) => Record<string, string>
}

export interface IntegrationEndpointInfo {
  name: string
  method: string
  description: string
  toolName: string
}

export interface IntegrationHostValidation {
  pattern: RegExp
  example: string
}

export interface IntegrationDefinition {
  id: IntegrationType
  title: string
  defaultConnectionName: string
  logo: string
  icon: string
  cardDescription: string
  modalDescription: string
  formFields: IntegrationFormField[]
  apiConfig: IntegrationAPIConfig
  defaultConfig: IntegrationConfig
  endpoints?: IntegrationEndpointInfo[]
  hostValidation?: IntegrationHostValidation
  EditableFieldsComponent?: ComponentType<IntegrationFieldsProps>
}

export const INTEGRATION_TYPES = {
  REQUEST311: 'request311',
  FASTTRACK: 'fasttrack',
  MUNIS_ARWS: 'munis_arws',
  CITYWORKS: 'cityworks',
  SHAREPOINT: 'sharepoint',
  CAYENTA_UTILITY_BILLING: 'cayenta_utility_billing',
  OPENGOV: 'opengov',
  CENTRAL_SQUARE: 'central_square',
  ENERGOV_EPL: 'energov_epl',
} as const satisfies Record<string, IntegrationType>
